import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, catchError, throwError } from 'rxjs';
import { tap, switchMap, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface ItemStandardCostHistoryResponse {
  costHistoryID: number;
  itemID: number;
  standardCost: number;
  validFrom: string;
  validTo: string;
  createdAt: string;
  userIDEntered: number;
}

export interface ProductCostResponse {
  productName: string;
  totalCost: number;
  costBreakdown: {
    rawMaterials: any[];
    directLabor: any[];
    indirectLabor: any[];
    fixedOverheads: any[];
    variableOverheads: any[];
    rawMaterialsTotal: number;
    directLaborTotal: number;
    indirectLaborTotal: number;
    fixedOverheadsTotal: number;
    variableOverheadsTotal: number;
  };
  currency: string;
}

export interface BudgetAssignment {
  budgetID: number;
  amount: number;
  productId: number | null;
  productName: string | null;
  totalCost: number | null;
  budgetName?: string;
  fiscalYear?: string;
}

export interface Budget {
  budgetID: number;
  budgetName: string;
  fiscalYear: string;
}

export interface BudgetLine {
  budgetLineID: number;
  budgetID: number;
  fiscalPeriodID: number;
  costCenterID: number | null;
  itemID: number | null;
  costCategory: string;
  budgetAmount: number;
  notes: string;
}

@Injectable({
  providedIn: 'root'
})
export class BudgetService {
  availableBudgets: Budget[] = [];

  constructor(private http: HttpClient) { }

  loadBudgets(): Observable<Budget[]> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found');
      return of([]);
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    return this.http.get<Budget[]>(`${environment.apiUrl}/api/Budgets/GetAll`, { headers }).pipe(
      tap({
        next: (budgets) => {

          this.availableBudgets = budgets || [];
        },
        error: (error) => {

          this.availableBudgets = [];
        }
      }),
      catchError(error => {

        return of([]);
      })
    );
  }

  getAllBudgets(headers: HttpHeaders): Observable<any> {
    // Use the correct endpoint casing
    return this.http.get(`${environment.apiUrl}/api/Budgets/GetAll`, { headers });
  }

  getProductCost(productId: number, headers: HttpHeaders): Observable<ProductCostResponse> {
    // First, get the item details to get the actual product name
    return this.http.get<any>(
      `${environment.apiUrl}/api/Items/GetById?ItemsId=${productId}`,
      { headers }
    ).pipe(
      switchMap(item => {
        const itemName = item?.itemName || `Item ${productId}`;

        // Then get the cost history
        return this.http.get<ItemStandardCostHistoryResponse[]>(
          `${environment.apiUrl}/api/ItemStandardCostHistory/GetByItemId`,
          { headers, params: { itemId: productId.toString() } }
        ).pipe(
          map((costHistoryArray: ItemStandardCostHistoryResponse[]) => {
            // Get the most recent cost history record (assuming array is sorted by date)
            const latestCostHistory = costHistoryArray && costHistoryArray.length > 0
              ? costHistoryArray[costHistoryArray.length - 1]
              : null;

            if (!latestCostHistory) {
              throw new Error(`No cost history found for item ID ${productId}`);
            }

            // Transform the ItemStandardCostHistory response to match ProductCostResponse interface
            const response: ProductCostResponse = {
              productName: itemName, // Use actual item name from Items API
              totalCost: latestCostHistory.standardCost,
              costBreakdown: {
                rawMaterials: [],
                directLabor: [],
                indirectLabor: [],
                fixedOverheads: [],
                variableOverheads: [],
                rawMaterialsTotal: 0,
                directLaborTotal: 0,
                indirectLaborTotal: 0,
                fixedOverheadsTotal: 0,
                variableOverheadsTotal: latestCostHistory.standardCost // Put all cost in variable overheads for now
              },
              currency: 'ZAR' // Using ZAR as per memory
            };

            return response;
          }),
        );
      }),
      catchError(error => {

        // Continue with cost history request even if item details fail
        return this.http.get<ItemStandardCostHistoryResponse[]>(
          `${environment.apiUrl}/api/ItemStandardCostHistory/GetByItemId`,
          { headers, params: { itemId: productId.toString() } }
        ).pipe(
          map((costHistoryArray: ItemStandardCostHistoryResponse[]) => {
            const latestCostHistory = costHistoryArray && costHistoryArray.length > 0
              ? costHistoryArray[costHistoryArray.length - 1]
              : null;

            if (!latestCostHistory) {
              throw new Error(`No cost history found for item ID ${productId}`);
            }

            return {
              productName: `Item ${productId}`, // Fallback if item fetch fails
              totalCost: latestCostHistory.standardCost,
              costBreakdown: {
                rawMaterials: [],
                directLabor: [],
                indirectLabor: [],
                fixedOverheads: [],
                variableOverheads: [],
                rawMaterialsTotal: 0,
                directLaborTotal: 0,
                indirectLaborTotal: 0,
                fixedOverheadsTotal: 0,
                variableOverheadsTotal: latestCostHistory.standardCost
              },
              currency: 'ZAR'
            };
          })
        );
      }),

      tap(response => {
        console.log('');
      }),
      catchError(error => {
        console.error('',);

        // Handle 404 errors for legacy products without ItemStandardCostHistory entries
        if (error.status === 404) {
          const legacyProductError = new Error(
            `No cost history found for this product\r\n\r\n` +
            `To resolve this issue, please:\r\n` +
            `• Option 1: Go to the Cost Calculator, adjust the costs, and save via the big save button\r\n\r\n` +
            `This will create the required cost history entry for budget assignments.`
          );


          (legacyProductError as any).isLegacyProductError = true;
          (legacyProductError as any).productId = productId;
          throw legacyProductError;
        }

        throw error;
      })
    );
  }

  getBudgetLinesByBudgetId(budgetId: number, headers: HttpHeaders): Observable<BudgetLine[]> {
    return this.http.get<BudgetLine[]>(
      `${environment.apiUrl}/api/BudgetLines/GetByBudgetId`,
      { headers, params: { budgetId: String(budgetId) } as any }
    ).pipe(
      tap(lines => console.log('', lines)),
      catchError(error => {
        console.error('', error);
        return of([]);
      })
    );
  }

  saveBudgetAssignments(assignments: BudgetAssignment[], headers: HttpHeaders): Observable<any> {
    // Filter out any assignments with invalid budget IDs
    const validAssignments = assignments.filter(assignment => {
      const isValid = assignment.budgetID && assignment.budgetID > 0;
      if (!isValid) {
        console.warn('Skipping assignment with invalid budget ID:', assignment);
      }
      return isValid;
    });

    if (validAssignments.length === 0) {
      return throwError(() => new Error('No valid budget assignments to save'));
    }

    // Process the first valid assignment (we'll handle one at a time to match backend)
    const assignment = validAssignments[0];

    // Convert both IDs to strings for comparison to handle number/string mismatches
    const budgetID = assignment.budgetID;


    // Find the budget in availableBudgets to get the name and fiscal year if not provided
    const budget = this.availableBudgets.find(b => String(b.budgetID) === String(budgetID));



    const amount = parseFloat(assignment.amount?.toString() || '0');
    const budgetName = assignment.budgetName || budget?.budgetName || 'Unknown Budget';
    const fiscalYear = (assignment.fiscalYear || budget?.fiscalYear || new Date().getFullYear()).toString();

    if (!budgetID || budgetID <= 0) {
      console.error('Invalid budget ID after processing:', budgetID);
      return throwError(() => new Error('Invalid budget ID'));
    }

    // Resolve period from budget lines (use fiscalPeriodID when available)
    return this.getBudgetLinesByBudgetId(budgetID, headers).pipe(
      switchMap((lines: BudgetLine[]) => {
        const resolvedPeriod = (lines && lines.length > 0)
          ? String(lines[0].fiscalPeriodID)
          : String(fiscalYear); // ensure string per backend DTO

        const payload = {
          budgetID: budgetID,
          budgetName: budgetName,
          period: resolvedPeriod,
          totalActualCostFromInventory: amount
        };

        console.log('Sending budget assignment to API:', payload);

        return this.http.post(`${environment.apiUrl}/api/ActualBudgetCost/Add`, payload, {
          headers: headers.set('Content-Type', 'application/json'),
          withCredentials: true
        }).pipe(
          tap(response => console.log('Save successful:', response))
        );
      }),
      catchError(error => {
        console.error('Error saving budget assignment:', error);
        return throwError(() => error);
      })
    );
  }
}
